import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import Dashboard from './Dashboard';
import { Ethnicity } from './data/Ethnicity';

type DashboardContextType = {
    dashboard: Dashboard | null;
    isLoading: boolean;
    updateSelectedEthnicities: (ethnicities: Set<Ethnicity>) => void;
    updateSelectedYear: (year: number) => void;
    updateSelectedStates: (states: Set<string>) => void;
};

const DashboardContext = createContext<DashboardContextType | null>(null);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [dashboard, setDashboard] = useState<Dashboard | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        Dashboard.init().then((dashboard) => {
            setDashboard(dashboard);
            setIsLoading(false);
        });
    }, []);

    //we need to create a new dashboard object to trigger a re-render
    //not sure if this is the best way to do it but it works for now
    const createNewDashboard = (dashboard: Dashboard): Dashboard => {
        return new Dashboard(
            dashboard.year,
            dashboard.selectedEthnicities,
            dashboard.demographicData,
            dashboard.selectedElectionData,
            dashboard.electionDataMap,
            dashboard.isInitialized,
            dashboard.selectedStates,
            dashboard.getAllElectionData
        );
    };

    const updateSelectedEthnicities = useCallback((ethnicities: Set<Ethnicity>) => {
        if (dashboard) {
            dashboard.setSelectedEthnicities(ethnicities);
            setDashboard(createNewDashboard(dashboard));
        }
    }, [dashboard]);

    const updateSelectedYear = useCallback((year: number) => {
        if (dashboard) {
            // console.log('updateSelectedYear', year);
            dashboard.setYear(year);
            setDashboard(createNewDashboard(dashboard));
        }
        // console.log('dashboard', dashboard?.selectedElectionData);
    }, [dashboard]);

    const updateSelectedStates = useCallback((states: Set<string>) => {
        if (dashboard) {
            dashboard.selectedStates = states;
            setDashboard(createNewDashboard(dashboard));
        }
    }, [dashboard]);

    return (
        <DashboardContext.Provider value={{
            dashboard,
            isLoading,
            updateSelectedEthnicities,
            updateSelectedYear,
            updateSelectedStates
        }}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = (): DashboardContextType => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboard must be used within a DashboardProvider');
    }
    return context;
};